import { Button } from "@mcleod/components";
import { Alignment, ArrayUtil } from "@mcleod/core";
import { MenuAction } from "./actions/MenuAction";
import { MenuActionAddBranch } from "./actions/MenuActionAddBranch";
import { MenuActionAddLeaf } from "./actions/MenuActionAddLeaf";
import { MenuActionDelete } from "./actions/MenuActionDelete";
import { MenuActionEdit } from "./actions/MenuActionEdit";
import { MenuActionInclude } from "./actions/MenuActionInclude";
import { MenuActionInsertScript } from "./actions/MenuActionInsertScript";
import { MenuActionMove } from "./actions/MenuActionMove";
import { MenuActionSwapPosition } from "./actions/MenuActionSwapPosition";
import { MenuActionViewChanges } from "./actions/MenuActionViewChanges";
import { AutogenLayoutMenuEditor } from "./autogen/AutogenLayoutMenuEditor";
import { MenuDataSource } from "./MenuDataSource";
import { MenuEditorTree } from "./MenuEditorTree";
import { MenuEditorTreeNode } from "./MenuEditorTreeNode";
import { ReadOnlyTextAreaPanel } from "./ReadOnlyTextAreaPanel";

export class MenuEditor extends AutogenLayoutMenuEditor {
    private _menuDataSource: MenuDataSource;
    treeMenu: MenuEditorTree;
    private isActionExecuting: boolean = false;
    private actionRegistry: Map<Button, () => MenuAction>;

    override async onLoad() {
        this._menuDataSource = new MenuDataSource();
        this.createActionRegistry();
        this.treeMenu = new MenuEditorTree({
            menuEditor: this,
            menuDataSource: this._menuDataSource,
            borderRadius: 4,
            borderColor: "subtle.light",
            backgroundColor: "subtle.lightest",
            color: "subtle.darker",
            fillHeight: true,
            padding: 8,
            id: "treeMenu",
            width: "500"
        });

        this._menuDataSource.addChangedDataCallback(() => {
            const hasChanged = this._menuDataSource.hasChanged();
            this.buttonSave.enabled = hasChanged;
            this.buttonViewChanges.visible = hasChanged;
            this.buttonExecutedChanges.visible = !ArrayUtil.isEmptyArray(this._menuDataSource.executedChanges);
        });

        this.actionRegistry.forEach((action, button) => {
            if (button.parent == this.panelActions) {
                button.setStyleProps({
                    width: "100%",
                    justifyContent: "flex-start"
                });
                button.tooltipPosition = Alignment.RIGHT;
            }
            button.addClickListener(() => this.executeAction(action()));
        });

        this.panelTree.add(this.treeMenu);
        await this._menuDataSource.loadData();
        this.treeMenu.addChangeListener(() => this.menuSelectionChanged());
        this.treeMenu.selectedNodeBackgroundColor = "component.palette.hover.backgroundColor"
        this.treeMenu.selectedNodeColor = (node) => node.color;
        this.menuSelectionChanged();
        this.buttonSave.addClickListener(() => this.onSaveClick());
    }

    private createActionRegistry() {
        this.actionRegistry = new Map<Button, () => MenuAction>([
            [this.buttonAddItem, () => new MenuActionAddLeaf(this.treeMenu.selectedNode)],
            [this.buttonAddSubMenu, () => new MenuActionAddBranch(this.treeMenu.selectedNode)],
            [this.buttonEditItem, () => new MenuActionEdit(this.treeMenu.selectedNode)],
            [this.buttonDeleteItem, () => new MenuActionDelete(this.treeMenu.selectedNode)],
            [this.buttonMove, () => new MenuActionMove(this.treeMenu.selectedNode)],
            [this.buttonInclude, () => new MenuActionInclude(this.treeMenu.selectedNode)],
            [this.buttonMoveUp, () => new MenuActionSwapPosition(this.treeMenu.selectedNode, "up")],
            [this.buttonMoveDown, () => new MenuActionSwapPosition(this.treeMenu.selectedNode, "down")],
            [this.buttonScript, () => new MenuActionInsertScript(this.treeMenu.selectedNode)],
            [this.buttonViewChanges, () => new MenuActionViewChanges(this.treeMenu.getRootNode().children[0] as MenuEditorTreeNode)]
        ]);
    }

    private menuSelectionChanged() {
        const selected = this.treeMenu.selectedNode;
        if (selected != null) {
            const selectedPath = selected.isMainMenuNode ? selected.text : selected.getPathAsString();
            this.labelSelection.text = "Current selection: " + selectedPath;
        } else {
            this.labelSelection.text = "Select a menu item to edit";
        }
        this.syncButtons();
    }

    private syncButtons() {
        this.actionRegistry.forEach((action, button) => {
            if (button != this.buttonViewChanges) {
                button.enabled = this.isActionExecuting === false && this.treeMenu.selectedNode != null;
                if (this.treeMenu.selectedNode != null || button == this.buttonViewChanges) {
                    const isValidForSelection = action().validate();
                    button.visible = isValidForSelection === true;
                }
            }
        });
    }

    private async onSaveClick() {
        this.addBusyOverlay(this._menuDataSource.post());
    }

    executeEdit() {
        this.executeAction(new MenuActionEdit(this.treeMenu.selectedNode));
    }

    executeDelete() {
        this.executeAction(new MenuActionDelete(this.treeMenu.selectedNode));
    }

    private executeAction(action: MenuAction) {
        if (action == null || this.treeMenu.selectedNode == null) {
            return;
        }
        this.isActionExecuting = true;
        this.syncButtons();
        action.execute().finally(() => {
            if (this.isActionExecuting !== false) {
                this.isActionExecuting = false;
                this.syncButtons();
            }
        });
    }

    buttonExecutedChangesOnClick() {
        ReadOnlyTextAreaPanel.showInDialog(this._menuDataSource.executedChanges, { title: "Executed Changes" });
    }
}
